import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, ArrowLeft, Shield, Zap, Moon, Brain, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    id: 1,
    question: "Compared to 5 years ago, your stamina feels:",
    options: [
      { text: "Stronger", score: 0 },
      { text: "About the same", score: 1 },
      { text: "Slightly lower", score: 2 },
      { text: "Noticeably lower", score: 3 }
    ]
  },
  {
    id: 2,
    question: "Your confidence in the bedroom is:",
    options: [
      { text: "Very high", score: 0 },
      { text: "Mostly consistent", score: 1 },
      { text: "Inconsistent at times", score: 2 },
      { text: "Causing stress", score: 3 }
    ]
  },
  {
    id: 3,
    question: "How often do you feel mentally drained?",
    options: [
      { text: "Rarely", score: 0 },
      { text: "A few times a week", score: 1 },
      { text: "Most days", score: 2 },
      { text: "Constantly", score: 3 }
    ]
  },
  {
    id: 4,
    question: "Your current stress level is:",
    options: [
      { text: "Low", score: 0 },
      { text: "Manageable", score: 1 },
      { text: "High", score: 2 },
      { text: "Overwhelming", score: 3 }
    ]
  },
  {
    id: 5,
    question: "You wake up feeling:",
    options: [
      { text: "Fully refreshed", score: 0 },
      { text: "Slightly tired", score: 1 },
      { text: "Groggy", score: 2 },
      { text: "Exhausted", score: 3 }
    ]
  },
  {
    id: 6,
    question: "You get 7–8 hours of quality sleep:",
    options: [
      { text: "Almost always", score: 0 },
      { text: "Sometimes", score: 1 },
      { text: "Rarely", score: 2 },
      { text: "Never", score: 3 }
    ]
  },
  {
    id: 7,
    question: "Your workouts feel:",
    options: [
      { text: "Powerful", score: 0 },
      { text: "Decent", score: 1 },
      { text: "Lower endurance than before", score: 2 },
      { text: "Struggling to finish", score: 3 }
    ]
  },
  {
    id: 8,
    question: "You experience mid-day energy crashes:",
    options: [
      { text: "Never", score: 0 },
      { text: "Occasionally", score: 1 },
      { text: "Frequently", score: 2 },
      { text: "Daily", score: 3 }
    ]
  },
  {
    id: 9,
    question: "Performance inconsistency has occurred:",
    options: [
      { text: "Never", score: 0 },
      { text: "Once or twice", score: 1 },
      { text: "Occasionally", score: 2 },
      { text: "Increasingly", score: 3 }
    ]
  },
  {
    id: 10,
    question: "You feel like your body isn't responding like it used to:",
    options: [
      { text: "Not at all", score: 0 },
      { text: "Slightly", score: 1 },
      { text: "Moderately", score: 2 },
      { text: "Definitely", score: 3 }
    ]
  }
];

const MensScorecard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // 0-9 questions, 10 = email gate, 11 = results
  const [answers, setAnswers] = useState(Array(10).fill(null));
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const totalScore = answers.reduce((sum, ans) => sum + (ans !== null ? ans : 0), 0);
  const progress = ((currentStep + 1) / 12) * 100;

  const handleAnswer = (score) => {
    const newAnswers = [...answers];
    newAnswers[currentStep] = score;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentStep < 9 && answers[currentStep] !== null) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 9 && answers[currentStep] !== null) {
      setCurrentStep(10); // Go to email gate
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/submit-assessment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName,
          email: email,
          score: totalScore,
          funnel_type: "men",
          answers: answers
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult({
          score: totalScore,
          category: data.score_category
        });
        setCurrentStep(11); // Show results
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting assessment:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Results content based on score
  const getResultContent = () => {
    if (totalScore <= 7) {
      return {
        headline: "Your Vitality Looks Strong",
        text: "Your foundation is solid. Focus on maintaining recovery, circulation, and stress balance.",
        buttonText: "View Maintenance Guide",
        buttonAction: () => navigate("/mens-maintenance-guide"),
        color: "green"
      };
    } else if (totalScore <= 16) {
      return {
        headline: "Subtle Signs of Decline Are Starting",
        text: "Stress and recovery patterns may be affecting stamina and confidence. Addressing this early makes a big difference.",
        buttonText: "View Free Vitality Reset Guide",
        buttonAction: () => navigate("/mens-vitality-guide"),
        color: "yellow"
      };
    } else {
      return {
        headline: "Your Body Is Signaling for Support",
        text: "Chronic stress and low recovery may be impacting your confidence and performance. This is common after 30 — and manageable with the right support.",
        buttonText: "Start My Vitality Protocol",
        buttonAction: () => navigate("/mens-vitality-protocol"),
        color: "red"
      };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-gray-500 mt-2 text-center">
            {currentStep < 10 ? `Question ${currentStep + 1} of 10` : currentStep === 10 ? "Almost there!" : "Your Results"}
          </p>
        </div>

        {/* Question Steps (0-9) */}
        {currentStep < 10 && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {currentStep === 0 && (
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Is Your Vitality Quietly Declining?</h1>
                <p className="text-gray-600">Answer 10 quick questions about your stamina, stress, sleep, and energy.</p>
              </div>
            )}

            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">{questions[currentStep].question}</h2>
              <div className="space-y-3">
                {questions[currentStep].options.map((option, idx) => (
                  <button
                    key={`${option.text}-${idx}`}
                    onClick={() => handleAnswer(option.score)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      answers[currentStep] === option.score
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50"
                    }`}
                  >
                    <span className="font-medium">{option.text}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 0}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={answers[currentStep] === null}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                {currentStep === 9 ? "See My Results" : "Next"} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Email Gate (step 10) */}
        {currentStep === 10 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Unlock Your Personalized Vitality Results</h2>
            <p className="text-gray-600 mb-8">Enter your email to see your score and recommended next steps.</p>

            <div className="space-y-4 max-w-sm mx-auto">
              <div className="text-left">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  placeholder="Enter your first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div className="text-left">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 mt-4"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                  </>
                ) : (
                  "Show My Results"
                )}
              </Button>
            </div>

            <button
              onClick={() => setCurrentStep(9)}
              className="mt-4 text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to questions
            </button>
          </div>
        )}

        {/* Results (step 11) */}
        {currentStep === 11 && result && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {(() => {
              const content = getResultContent();
              return (
                <>
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                    content.color === "green" ? "bg-green-100" :
                    content.color === "yellow" ? "bg-yellow-100" : "bg-red-100"
                  }`}>
                    {content.color === "green" ? (
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    ) : content.color === "yellow" ? (
                      <Brain className="w-10 h-10 text-yellow-600" />
                    ) : (
                      <Zap className="w-10 h-10 text-red-600" />
                    )}
                  </div>

                  <div className="mb-4">
                    <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${
                      content.color === "green" ? "bg-green-100 text-green-700" :
                      content.color === "yellow" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"
                    }`}>
                      Score: {totalScore}/30
                    </span>
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{content.headline}</h2>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">{content.text}</p>

                  <Button
                    onClick={content.buttonAction}
                    className={`px-8 py-3 text-lg ${
                      content.color === "green" ? "bg-green-600 hover:bg-green-700" :
                      content.color === "yellow" ? "bg-yellow-600 hover:bg-yellow-700" : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {content.buttonText}
                  </Button>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      Check your email for personalized recommendations based on your results.
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default MensScorecard;
